/**
 * Admin work-report (Rapport / Zeiterfassung) API helpers — S3.
 *
 * Mirrors the ./tasks.js conventions: raw fetch, Bearer headers from
 * ./core, `{items, total}` list envelope, thrown Error with a parsed
 * German message and `err.status` attached for status-specific UI.
 *
 * Server-owned fields are NEVER sent from here: `duration_minutes` and
 * `work_date` are derived by the backend from started_at/ended_at
 * (Europe/Zurich civil date), `organization_id` comes from the JWT and
 * `created_by_user_id` from the authenticated actor. `sanitizePayload`
 * strips them defensively even if a caller passes them.
 */
import {
  API_BASE_URL,
  adminErrorFrom,
  getApiHeaders,
  getApiHeadersMultipart,
} from "./core";

/** Build a query string from a params object (mirrors tasks.js `_qs`).
 * Arrays become repeated keys (FastAPI list query params). */
function _qs(params = {}) {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null && v !== "") search.append(key, v);
      });
      return;
    }
    search.append(key, value);
  });
  const s = search.toString();
  return s ? `?${s}` : "";
}

// Fields the server derives itself — never accepted from the client.
const SERVER_OWNED_FIELDS = [
  "duration_minutes",
  "work_date",
  "organization_id",
  "created_by_user_id",
  "created_at",
  "updated_at",
  "id",
  // REPORT-PEOPLE: provenance the server assigns. The API ignores it
  // either way; stripping it here keeps a stale spread from a read
  // response out of a write, which is what this list is for.
  "creation_batch_id",
];

/** Strip server-owned keys from an outbound payload (defense-in-depth —
 * the form never sets them, but a stale spread must not leak them). */
export function sanitizeWorkReportPayload(payload = {}) {
  const out = { ...payload };
  SERVER_OWNED_FIELDS.forEach((k) => delete out[k]);
  return out;
}

async function _throwFrom(res) {
  // RESP2d: delegates to the shared builder so a structured refusal
  // keeps its `code` — the report form needs to tell a category that was
  // deactivated a moment ago from one that never existed. `message` and
  // `status` are exactly what they were, so every other caller of every
  // report client is unaffected.
  throw await adminErrorFrom(res);
}

/** GET /api/admin/work-reports → `{items, total}`. */
export async function fetchAdminWorkReports(params = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports${_qs(params)}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  const data = await res.json();
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    total: typeof data?.total === "number" ? data.total : 0,
  };
}

/** GET /api/admin/work-reports/summary → minute totals + by_user. */
export async function fetchAdminWorkReportSummary(params = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/summary${_qs(params)}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/**
 * GET /api/admin/work-reports/period-summary
 *
 * report_count, total_minutes and by_employee for EXACTLY the rows the
 * list endpoint returns for the same filters — it takes the identical
 * parameters minus limit/offset, and the backend applies the identical
 * WHERE clauses.
 *
 * This is the source for every "hours in the period on screen" number.
 * The older /summary endpoint answers the fixed today/this-week
 * question and cannot be asked about a range, which is why the calendar
 * KPI used to show the current week's total under an unqualified
 * "Rapportstunden" label.
 *
 * Never pass limit/offset: the total of a page is not the total.
 */
export async function fetchAdminWorkReportPeriodSummary(params = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/period-summary${_qs(params)}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/**
 * GET /api/admin/tasks/{taskId}/work-report-summary (D1)
 *
 * All-time effort booked on ONE task: total_minutes, report_count,
 * first_report_date, last_report_date. A dedicated route rather than a
 * task_id filter on /work-reports/summary — that one is a week-window
 * calendar aggregate and a task total is not windowed. 404 for an
 * unknown or cross-org task id.
 */
export async function fetchAdminTaskWorkReportSummary(taskId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tasks/${encodeURIComponent(
      taskId
    )}/work-report-summary`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/**
 * GET /api/admin/tenants/{tenantId}/work-report-summary (D2a)
 *
 * Effort booked DIRECTLY on one tenant: only reports carrying this
 * ``tenant_id``. A report that reaches the tenant through a tenancy, a
 * unit or a task is deliberately excluded — the list on the same tab
 * applies the identical filter, so number and rows always agree.
 * 404 for an unknown or cross-org tenant id.
 */
export async function fetchAdminTenantWorkReportSummary(tenantId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenants/${encodeURIComponent(
      tenantId
    )}/work-report-summary`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/**
 * GET /api/admin/work-reports/effort-summary (D2b–D2e)
 *
 * All-time effort booked DIRECTLY on one entity. Exactly one of
 * task_id / tenant_id / tenancy_id / unit_id / application_id /
 * landlord_id — the same six direct foreign keys the list endpoint
 * filters by, so a KPI row and its list always describe the same set.
 * 404 for an unknown or cross-org id.
 *
 * The task and tenant views keep their own earlier routes (D1/D2a); the
 * four later entities share this one rather than adding four more paths.
 */
export async function fetchAdminWorkReportEffortSummary(params = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/effort-summary${_qs(params)}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/**
 * GET /api/admin/work-reports/{id} → WorkReportDetailRead (D2f).
 *
 * A superset of the row the list returns: same fields plus the resolved
 * labels for assignee, creator and each linked entity, so the detail
 * page needs no follow-up request per link.
 *
 * 404 for an unknown id AND for another organisation's id — the caller
 * cannot tell the two apart, which is the point.
 */
export async function fetchAdminWorkReportById(reportId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${reportId}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/**
 * GET /api/admin/work-reports/{id}/activity → `{items, total}` (D2f).
 *
 * Newest-first audit trail for one report, straight from the existing
 * S3 endpoint — no new activity architecture, no merging with tasks or
 * notes.
 */
export async function fetchAdminWorkReportActivity(reportId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${reportId}/activity`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/**
 * GET /api/admin/activity-feed → `{items, total, has_more}` (D3).
 *
 * The merged, read-only timeline for ONE entity: tasks, work reports,
 * notes and relevant changes, newest first. `entity_type` is one of the
 * seven the backend allows; anything else is a 422 by design.
 *
 * Strictly direct — the backend never expands across relations — so a
 * caller cannot accidentally widen the feed by passing a related id.
 */
export async function fetchAdminActivityFeed(params = {}) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/activity-feed${_qs(params)}`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/** POST /api/admin/work-reports → created WorkReportRead (201). */
export async function createAdminWorkReport(payload) {
  const res = await fetch(`${API_BASE_URL}/api/admin/work-reports`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(sanitizeWorkReportPayload(payload)),
  });
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/**
 * POST /api/admin/work-reports/batch → one report PER PERSON, atomically.
 *
 * Used when several people are captured at once (REPORT-PEOPLE). One
 * person keeps going through `createAdminWorkReport` above, so that
 * endpoint's contract and every existing caller stay untouched.
 *
 * Always resolves to the envelope, never to a bare object or list:
 * `{creation_batch_id, reports, person_booking_count, elapsed_minutes,
 * total_person_minutes}`. The three numbers are distinct on purpose —
 * an hour worked by three people is 60 elapsed minutes, 3 bookings and
 * 180 person-minutes, and one label cannot carry all three.
 *
 * A rejected request leaves NOTHING behind: the server writes all rows
 * in one transaction, so there is no partial group for the caller to
 * reconcile.
 */
export async function createAdminWorkReportBatch(payload) {
  const res = await fetch(`${API_BASE_URL}/api/admin/work-reports/batch`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(sanitizeWorkReportPayload(payload)),
  });
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/** PATCH /api/admin/work-reports/{id} → updated WorkReportRead. */
export async function updateAdminWorkReport(reportId, payload) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${reportId}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(sanitizeWorkReportPayload(payload)),
    }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/**
 * POST /api/admin/work-reports/{id}/cancel — SAFE-DELETE1.
 *
 * The replacement for the removed delete helper: a report is a proof of
 * worked time, so it is CANCELLED with a mandatory reason, never hard
 * deleted (the backend's DELETE route is a fixed 409). Who and when are
 * server-side; this sends only the reason. Returns the updated report
 * with `is_cancelled`, `cancelled_at`, `cancellation_reason`.
 */
export async function cancelAdminWorkReport(reportId, reason) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${encodeURIComponent(reportId)}/cancel`,
    {
      method: "POST",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

// ── Notes (REPORT-NOTES1) ─────────────────────────────────────────────

/** GET .../comments → `{comments, total}`, oldest-first. */
export async function fetchAdminWorkReportComments(reportId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${encodeURIComponent(reportId)}/comments`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/** POST .../comments — author is the JWT user, body is the note text. */
export async function createAdminWorkReportComment(reportId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${encodeURIComponent(reportId)}/comments`,
    {
      method: "POST",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/** PATCH .../comments/{id} — author-only on the backend (403 otherwise). */
export async function updateAdminWorkReportComment(reportId, commentId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${encodeURIComponent(reportId)}` +
      `/comments/${encodeURIComponent(commentId)}`,
    {
      method: "PATCH",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/** DELETE .../comments/{id} — author-only; 204 → `{ok: true}` sentinel. */
export async function deleteAdminWorkReportComment(reportId, commentId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${encodeURIComponent(reportId)}` +
      `/comments/${encodeURIComponent(commentId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return { ok: true };
}

// ── Attachments (REPORT-ATTACH1) ──────────────────────────────────────
//
// Same two-call download shape as the task attachments: the backend
// never puts the R2 object key on the wire, so a download asks for a
// short-lived signed URL first.

/** GET .../attachments → array, newest first, never an `object_key`. */
export async function fetchAdminWorkReportAttachments(reportId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${encodeURIComponent(reportId)}/attachments`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  const data = await res.json();
  return Array.isArray(data?.items) ? data.items : [];
}

/** POST .../attachments — multipart, one file. Server owns the rules. */
export async function uploadAdminWorkReportAttachment(reportId, file) {
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${encodeURIComponent(reportId)}/attachments`,
    { method: "POST", headers: getApiHeadersMultipart(), body: fd }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/** GET .../attachments/{id}/download → `{url}` valid for minutes. */
export async function fetchAdminWorkReportAttachmentDownloadUrl(
  reportId,
  attachmentId
) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${encodeURIComponent(reportId)}` +
      `/attachments/${encodeURIComponent(attachmentId)}/download`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return res.json();
}

/** DELETE .../attachments/{id} — 204 → `{ok: true}` sentinel. */
export async function deleteAdminWorkReportAttachment(reportId, attachmentId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/work-reports/${encodeURIComponent(reportId)}` +
      `/attachments/${encodeURIComponent(attachmentId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) await _throwFrom(res);
  return { ok: true };
}
